/**
 * Home tab — the at-a-glance landing view: a P/L strip over the latest run
 * summaries plus a narrated feed of recent runs, echoing the landing page's
 * "alerts that read like a story" panel on real data.
 *
 * DATA CONTRACT (all server shapes already typed elsewhere — nothing new):
 *   - The glance strip reads ONLY runs-index rows (`useRuns`): each row's
 *     `summary` carries the cheap display fields the agent writes for exactly
 *     this purpose (artifacts.py write_run_index — account cash/equity,
 *     positions_count, execution_mode; EOD adds pnl_amount/pnl_pct computed
 *     from the morning snapshot). No S3 artifact fetch is needed for the strip.
 *   - Total return derives from `summary.committed_capital` (the agent's
 *     derived basis: equity at first observation, carried in every run
 *     summary) vs the latest equity — the same basis the §6 reserve floor
 *     gates on, so the Home number and the validator can never disagree.
 *   - The feed fetches the latest few run ARTIFACTS (useRunArtifacts) and
 *     reuses RunsTab's narrative extractors (runNarrative / runTldr / eodBlock
 *     / eodTldr) so Home and the Runs detail can never tell different stories.
 *
 * Everything renders tolerantly: no runs yet → placeholder strip + empty-feed
 * note; missing summary fields → "—"; artifact still loading → the beat shows
 * its index-row facts until the narrative arrives.
 */

import { useMemo, useState } from 'react';
import {
  useRuns,
  useRunArtifacts,
  useShadowPositions,
  useStrategyPositions,
  type RunIndexRow,
  type ShadowPosition,
  type ShadowTrade,
} from '../lib/queries';
import { eodBlock, eodTldr, runNarrative, runTldr, statusPill } from './RunsTab';

// How many runs feed the narrated beats (each costs one artifact GET; the
// query cache is shared with the Runs tab detail view).
const FEED_RUNS = 6;

// ---------------------------------------------------------------------------
// Pure helpers (exported for unit tests — no DOM needed).
// ---------------------------------------------------------------------------

function asNumber(v: unknown): number | null {
  if (typeof v === 'number' && !Number.isNaN(v)) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
  }
  return null;
}

function summaryOf(row: RunIndexRow): Record<string, unknown> | null {
  const s = row.summary;
  return s && typeof s === 'object' && !Array.isArray(s) ? (s as Record<string, unknown>) : null;
}

/** Rows newest-first by completed_at (defensive — the API already returns
 *  newest-first, but the strip's "latest" semantics must not depend on it). */
export function sortRunsDesc(rows: RunIndexRow[]): RunIndexRow[] {
  return [...rows].sort((a, b) =>
    String(b.completed_at ?? '').localeCompare(String(a.completed_at ?? '')),
  );
}

export interface Pnl {
  amount: number | null;
  pct: number | null;
}

export interface ShadowPnl {
  realized: number | null;
  unrealized: number | null;
  total: number | null;
  openCount: number | null;
  closedCount: number | null;
}

export interface GlanceStats {
  equity: number | null;
  cash: number | null;
  positionsCount: number | null;
  executionMode: string | null;
  /** completed_at of the row the equity came from. */
  asOf: string | null;
  /** BOOK day P/L from the newest EOD row carrying pnl fields — the drift
   *  of held positions (in shadow mode nothing else moves equity). */
  dayPnl: Pnl | null;
  /** BOOK P/L since inception: equity vs derived committed capital —
   *  summary.committed_capital (null when either side is missing). */
  totalPnl: Pnl | null;
  /** SHADOW P/L — the cumulative ledger of validator-accepted intended
   *  trades (summary.shadow_pnl, written by the agent's shadow ledger). */
  shadowPnl: ShadowPnl | null;
}

/** The at-a-glance numbers, from index rows only. Newest row with an equity
 *  summary wins for the account snapshot; newest row with pnl fields wins for
 *  day P/L (EOD runs write those); newest row carrying committed_capital
 *  supplies the since-inception basis (runs predating the derivation lack
 *  it — the cell shows an em dash until the next run writes one). */
export function glanceStats(rows: RunIndexRow[]): GlanceStats {
  const sorted = sortRunsDesc(rows);

  let equity: number | null = null;
  let cash: number | null = null;
  let positionsCount: number | null = null;
  let executionMode: string | null = null;
  let asOf: string | null = null;
  for (const row of sorted) {
    const s = summaryOf(row);
    if (!s) continue;
    const eq = asNumber(s.equity);
    if (eq === null) continue;
    equity = eq;
    cash = asNumber(s.cash);
    positionsCount = asNumber(s.positions_count);
    executionMode = typeof s.execution_mode === 'string' ? s.execution_mode : null;
    asOf = row.completed_at ?? null;
    break;
  }

  let dayPnl: Pnl | null = null;
  for (const row of sorted) {
    const s = summaryOf(row);
    if (!s) continue;
    const amount = asNumber(s.pnl_amount);
    const pct = asNumber(s.pnl_pct);
    if (amount === null && pct === null) continue;
    dayPnl = { amount, pct };
    break;
  }

  let committed: number | null = null;
  for (const row of sorted) {
    const c = asNumber(summaryOf(row)?.committed_capital);
    if (c !== null && c > 0) {
      committed = c;
      break;
    }
  }

  const totalPnl: Pnl | null =
    equity !== null && committed !== null
      ? { amount: equity - committed, pct: ((equity - committed) / committed) * 100 }
      : null;

  let shadowPnl: ShadowPnl | null = null;
  for (const row of sorted) {
    const sp = summaryOf(row)?.shadow_pnl;
    if (!sp || typeof sp !== 'object') continue;
    const rec = sp as Record<string, unknown>;
    shadowPnl = {
      realized: asNumber(rec.realized),
      unrealized: asNumber(rec.unrealized),
      total: asNumber(rec.total),
      openCount: asNumber(rec.open_count),
      closedCount: asNumber(rec.closed_count),
    };
    break;
  }

  return { equity, cash, positionsCount, executionMode, asOf, dayPnl, totalPnl, shadowPnl };
}

export function fmtMoney(n: number | null, digits = 0): string {
  if (n === null) return '—';
  return n.toLocaleString('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

/** "+$214 (+0.21%)" with sign carried through; em-dash when empty. */
export function fmtPnl(pnl: Pnl | null): string {
  if (!pnl || (pnl.amount === null && pnl.pct === null)) return '—';
  const parts: string[] = [];
  if (pnl.amount !== null) {
    parts.push(`${pnl.amount < 0 ? '-' : '+'}$${fmtMoney(Math.abs(pnl.amount), 2)}`);
  }
  if (pnl.pct !== null) {
    parts.push(`(${pnl.pct < 0 ? '' : '+'}${pnl.pct.toFixed(2)}%)`);
  }
  return parts.join(' ');
}

export type BeatKind = 'gain' | 'loss' | 'live' | 'neutral';

export interface Beat {
  runId: string;
  /** "MM-DD HH:MM" UTC from completed_at (the run ids are timestamps too,
   *  but completed_at is the authoritative moment). */
  time: string;
  /** morning / eod / … (the routine field, lowercased). */
  label: string;
  kind: BeatKind;
  /** The narrated line(s): persisted narrative first, TLDR fallback. */
  lines: string[];
}

export function beatTime(completedAt: string | null | undefined): string {
  if (!completedAt) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(completedAt);
  return m ? `${m[2]}-${m[3]} ${m[4]}:${m[5]}` : completedAt.slice(0, 16);
}

/** Beat coloring: failures bleed red; an EOD with a signed day P/L colors by
 *  the sign; a successful morning run is the live ember; all else neutral. */
export function beatKind(row: RunIndexRow): BeatKind {
  const status = typeof row.status === 'string' ? row.status : '';
  if (status === 'failed') return 'loss';
  const s = summaryOf(row);
  if (s?.holiday === true) return 'neutral';
  const pnl = s ? asNumber(s.pnl_amount) : null;
  if (pnl !== null) return pnl < 0 ? 'loss' : 'gain';
  if (status === 'success') return 'live';
  return 'neutral';
}

/** The narrated lines for one run: the persisted decision narrative when the
 *  artifact carries one, else the reconstructed TLDR (EOD's marker first),
 *  else the index-row facts. Capped to keep the feed scannable. */
export function beatLines(row: RunIndexRow, artifact: unknown): string[] {
  const s = summaryOf(row);
  if (s?.holiday === true) return ['Market holiday — no run.'];

  const narrative = runNarrative(artifact);
  if (narrative) return [narrative];

  if (artifact !== null && artifact !== undefined) {
    const eod = eodBlock(artifact);
    const lines = eod ? eodTldr(eod) : runTldr(artifact);
    if (lines.length > 0) return lines.slice(0, 2);
  }

  // Artifact not loaded (yet, or fetch failed): cheap index-row facts.
  const status = typeof row.status === 'string' ? row.status : 'recorded';
  const bits = [`Run ${status}.`];
  const pnl = s ? { amount: asNumber(s.pnl_amount), pct: asNumber(s.pnl_pct) } : null;
  if (pnl && (pnl.amount !== null || pnl.pct !== null)) {
    bits.push(`Day P/L ${fmtPnl(pnl)}.`);
  }
  return bits;
}

// ---------------------------------------------------------------------------
// Shadow-positions drilldown helpers (pure — unit-tested without DOM).
// ---------------------------------------------------------------------------

export interface OccParts {
  underlying: string;
  /** YYYY-MM-DD */
  expiry: string;
  right: 'C' | 'P';
  /** Strike in dollars (the trailing 8 digits are thousandths). */
  strike: number;
}

const OCC_RE = /^([A-Z.]{1,6})(\d{2})(\d{2})(\d{2})([CP])(\d{8})$/;

/** Parse an OCC option symbol (e.g. NVDA260619C00100000) into its parts, or
 *  null when it doesn't match. Year = 2000+YY; strike = int(last 8)/1000. */
export function parseOcc(symbol: string): OccParts | null {
  const m = OCC_RE.exec(symbol);
  if (!m) return null;
  const [, underlying, yy, mm, dd, right, strikeRaw] = m;
  return {
    underlying,
    expiry: `20${yy}-${mm}-${dd}`,
    right: right as 'C' | 'P',
    strike: Number(strikeRaw) / 1000,
  };
}

/** A readable position label: "{underlying} {strike}{C|P} {expiry}" for an
 *  option leg, else the equity underlying/symbol, else the raw contract
 *  symbol, else an em dash. Never throws on garbage input. */
export function positionLabel(trade: ShadowTrade): string {
  const contract = typeof trade.contract_symbol === 'string' ? trade.contract_symbol : null;
  if (contract) {
    const occ = parseOcc(contract);
    if (occ) return `${occ.underlying} ${occ.strike}${occ.right} ${occ.expiry}`;
    // An option leg with a non-OCC symbol: show the raw contract before
    // falling through to the bare underlying (spec ordering).
    return contract;
  }
  const underlying =
    typeof trade.underlying === 'string'
      ? trade.underlying
      : typeof trade.symbol === 'string'
        ? trade.symbol
        : null;
  return underlying ?? '—';
}

/** The option expiry for a position's contract — the closest thing to a
 *  target sell date (options ride to expiry by default). `null` for equity
 *  legs or unparseable symbols. */
export function targetExit(trade: ShadowTrade): string | null {
  const contract = typeof trade.contract_symbol === 'string' ? trade.contract_symbol : null;
  return contract ? (parseOcc(contract)?.expiry ?? null) : null;
}

/** "+$155.00" / "-$50.00" — signed money from a string-or-number field, em
 *  dash when unparseable. */
export function fmtSignedMoney(v: unknown): string {
  const n = asNumber(v);
  if (n === null) return '—';
  return `${n < 0 ? '-' : '+'}$${fmtMoney(Math.abs(n), 2)}`;
}

/** Unsigned money with a leading $ (entry/mark premiums), em dash when null. */
export function fmtPrice(v: unknown): string {
  const n = asNumber(v);
  return n === null ? '—' : `$${fmtMoney(n, 2)}`;
}

/** Sign-color class for a numeric (string|number) value; '' when unparseable. */
function signClass(v: unknown): string {
  const n = asNumber(v);
  if (n === null) return '';
  return n < 0 ? ' glance-value--loss' : ' glance-value--gain';
}

/** "2026-06-10" date portion of an ISO timestamp, em dash when empty. */
function dateOnly(iso: string | null | undefined): string {
  if (!iso) return '—';
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(iso);
  return m ? m[1] : iso.slice(0, 10);
}

function qtyText(q: unknown): string {
  const n = asNumber(q);
  return n === null ? '—' : String(n);
}

function strategyText(pos: ShadowPosition): string {
  const strategy = typeof pos.trade.strategy === 'string' ? pos.trade.strategy : null;
  const direction = typeof pos.direction === 'string' ? pos.direction : null;
  if (strategy && direction && strategy !== direction) return `${strategy} · ${direction}`;
  return strategy ?? direction ?? '—';
}

// ---------------------------------------------------------------------------
// Book-positions drilldown helpers (pure — unit-tested without DOM). The held
// book positions live at the TOP LEVEL of the run artifact (artifact.positions),
// mirroring infra/.../reads.ts extractPositions. All numeric fields arrive as
// strings on the wire and are parsed before formatting; missing/garbage → '—'.
// ---------------------------------------------------------------------------

export interface BookPosition {
  symbol?: unknown;
  qty?: unknown;
  avg_entry_price?: unknown;
  current_price?: unknown;
  unrealized_pl?: unknown;
  unrealized_plpc?: unknown;
  unrealized_intraday_pl?: unknown;
  unrealized_intraday_plpc?: unknown;
  [key: string]: unknown;
}

/** The held book positions from a run artifact's top-level `positions` array.
 *  Tolerates a missing/non-array field (→ []) and skips non-object entries. */
export function bookPositions(artifact: unknown): BookPosition[] {
  if (!artifact || typeof artifact !== 'object') return [];
  const positions = (artifact as Record<string, unknown>).positions;
  if (!Array.isArray(positions)) return [];
  return positions.filter(
    (p): p is BookPosition => p !== null && typeof p === 'object' && !Array.isArray(p),
  );
}

export interface BookRow {
  symbol: string;
  qty: string;
  avgEntry: string;
  price: string;
  dayPnl: string;
  /** "(±x.xx%)" or '' when the pct is unparseable. */
  dayPct: string;
  dayClass: string;
  sinceOpenPnl: string;
  sinceOpenPct: string;
  sinceOpenClass: string;
}

/** "(+1.25%)" from a fraction-valued (string|number) field (×100), '' when
 *  unparseable. */
function fmtPctParen(v: unknown): string {
  const n = asNumber(v);
  if (n === null) return '';
  const pct = n * 100;
  return `(${pct < 0 ? '' : '+'}${pct.toFixed(2)}%)`;
}

/** Format one held position into display strings — symbol, qty, prices, and
 *  the two P/L columns (today's intraday, and since-OPENED vs cost basis), each
 *  with its sign-color class. Pure; never throws on garbage input. */
export function bookRow(pos: BookPosition): BookRow {
  return {
    symbol: typeof pos.symbol === 'string' && pos.symbol ? pos.symbol : '—',
    qty: qtyText(pos.qty),
    avgEntry: fmtPrice(pos.avg_entry_price),
    price: fmtPrice(pos.current_price),
    dayPnl: fmtSignedMoney(pos.unrealized_intraday_pl),
    dayPct: fmtPctParen(pos.unrealized_intraday_plpc),
    dayClass: signClass(pos.unrealized_intraday_pl),
    sinceOpenPnl: fmtSignedMoney(pos.unrealized_pl),
    sinceOpenPct: fmtPctParen(pos.unrealized_plpc),
    sinceOpenClass: signClass(pos.unrealized_pl),
  };
}

// ---------------------------------------------------------------------------
// The tab.
// ---------------------------------------------------------------------------

export default function HomeTab() {
  const runs = useRuns(20);
  const [bookOpen, setBookOpen] = useState(false);
  const [shadowOpen, setShadowOpen] = useState(false);
  const [strategyOpen, setStrategyOpen] = useState(false);
  // Fetch lazily: the network request only fires once the panel is expanded.
  const shadow = useShadowPositions(shadowOpen);
  const strategy = useStrategyPositions(strategyOpen);

  const rows = useMemo(() => sortRunsDesc(runs.data?.runs ?? []), [runs.data]);
  const feedRows = rows.slice(0, FEED_RUNS);
  const artifactResults = useRunArtifacts(feedRows.map((r) => r.run_id));
  const artifactById = new Map<string, unknown>();
  feedRows.forEach((row, i) => {
    artifactById.set(row.run_id, artifactResults[i]?.data?.artifact ?? null);
  });

  const stats = glanceStats(rows);

  // The held book positions come from the newest run artifact already fetched
  // for the feed (no lazy fetch — the artifacts load regardless of expand
  // state). Prefer the newest artifact that actually carries a positions array;
  // fall back to the newest loaded artifact (drives the empty state) and, when
  // nothing has arrived yet, to null (the loading state).
  let bookArtifact: unknown = null;
  for (const row of feedRows) {
    const artifact = artifactById.get(row.run_id);
    if (artifact == null) continue;
    if (bookArtifact == null) bookArtifact = artifact;
    if (Array.isArray((artifact as Record<string, unknown>).positions)) {
      bookArtifact = artifact;
      break;
    }
  }

  if (runs.isLoading) return <p className="home-loading">Loading…</p>;
  if (runs.isError) {
    return (
      <p className="home-error error" role="alert">
        Failed to load runs:{' '}
        {runs.error instanceof Error ? runs.error.message : 'unknown error'}
      </p>
    );
  }

  const pnlClass = (pnl: Pnl | null) =>
    !pnl || pnl.amount === null
      ? ''
      : pnl.amount < 0
        ? ' glance-value--loss'
        : ' glance-value--gain';

  return (
    <section className="home-tab" aria-label="Home">
      <div className="glance-strip" aria-label="Performance at a glance">
        <div className="glance-cell glance-cell--equity">
          <span className="glance-label">equity</span>
          <span className="glance-value glance-value--big">
            {stats.equity === null ? '—' : `$${fmtMoney(stats.equity)}`}
          </span>
          {stats.asOf && <span className="glance-asof">as of {beatTime(stats.asOf)} UTC</span>}
        </div>
        <button
          type="button"
          className="glance-cell glance-cell--toggle glance-cell--book"
          aria-expanded={bookOpen}
          aria-controls="book-panel"
          onClick={() => setBookOpen((v) => !v)}
        >
          <span className="glance-label">
            book
            <span className={`glance-caret${bookOpen ? ' glance-caret--open' : ''}`} aria-hidden="true">
              ▸
            </span>
          </span>
          <span className="book-face">
            <span className="book-face-line">
              <span className="book-face-tag">day</span>
              <span className={`glance-value${pnlClass(stats.dayPnl)}`}>{fmtPnl(stats.dayPnl)}</span>
            </span>
            <span className="book-face-line">
              <span className="book-face-tag">inception</span>
              <span className={`glance-value${pnlClass(stats.totalPnl)}`}>
                {fmtPnl(stats.totalPnl)}
              </span>
            </span>
          </span>
          <span className="glance-sub">
            {stats.positionsCount ?? '—'} position{stats.positionsCount === 1 ? '' : 's'}
          </span>
          <span className="glance-hint">{bookOpen ? 'hide positions' : 'view positions'}</span>
        </button>
        <button
          type="button"
          className="glance-cell glance-cell--toggle glance-cell--strategy"
          aria-expanded={strategyOpen}
          aria-controls="strategy-panel"
          onClick={() => setStrategyOpen((v) => !v)}
        >
          <span className="glance-label">
            strategy p/l
            <span
              className={`glance-caret${strategyOpen ? ' glance-caret--open' : ''}`}
              aria-hidden="true"
            >
              ▸
            </span>
          </span>
          {stats.executionMode === 'shadow' ? (
            <>
              <span className="glance-value">$0.00</span>
              <span className="glance-sub">shadow mode — no executions</span>
            </>
          ) : (
            <span className="glance-value">—</span>
          )}
          <span className="glance-hint">{strategyOpen ? 'hide positions' : 'view positions'}</span>
        </button>
        <button
          type="button"
          className="glance-cell glance-cell--toggle"
          aria-expanded={shadowOpen}
          aria-controls="shadow-panel"
          onClick={() => setShadowOpen((v) => !v)}
        >
          <span className="glance-label">
            shadow p/l
            <span className={`glance-caret${shadowOpen ? ' glance-caret--open' : ''}`} aria-hidden="true">
              ▸
            </span>
          </span>
          <span
            className={`glance-value${
              stats.shadowPnl?.total == null
                ? ''
                : stats.shadowPnl.total < 0
                  ? ' glance-value--loss'
                  : ' glance-value--gain'
            }`}
          >
            {stats.shadowPnl?.total == null
              ? '—'
              : `${stats.shadowPnl.total < 0 ? '-' : '+'}$${fmtMoney(Math.abs(stats.shadowPnl.total), 2)}`}
          </span>
          {stats.shadowPnl && (
            <span className="glance-sub">
              realized {fmtMoney(stats.shadowPnl.realized, 2)} · open{' '}
              {fmtMoney(stats.shadowPnl.unrealized, 2)} ({stats.shadowPnl.openCount ?? 0} open)
            </span>
          )}
          <span className="glance-hint">{shadowOpen ? 'hide positions' : 'view positions'}</span>
        </button>
        <div className="glance-cell">
          <span className="glance-label">cash</span>
          <span className="glance-value">
            {stats.cash === null ? '—' : `$${fmtMoney(stats.cash)}`}
          </span>
        </div>
        {stats.executionMode && (
          <div className="glance-cell">
            <span className="glance-label">mode</span>
            <span className="glance-value glance-value--mode">{stats.executionMode}</span>
          </div>
        )}
      </div>

      {bookOpen && (
        <div id="book-panel" className="detail-panel" aria-label="Book positions">
          <BookPanelBody artifact={bookArtifact} loaded={bookArtifact != null} />
        </div>
      )}

      {shadowOpen && (
        <div id="shadow-panel" className="detail-panel" aria-label="Shadow positions">
          <LedgerPositionsBody
            query={shadow}
            emptyLabel="No shadow positions yet — the first accepted trade will appear here."
          />
        </div>
      )}

      {strategyOpen && (
        <div id="strategy-panel" className="detail-panel" aria-label="Strategy positions">
          <LedgerPositionsBody
            query={strategy}
            emptyLabel="No strategy positions yet — nothing has executed (shadow mode)."
          />
        </div>
      )}

      <div className="home-feed" aria-label="Recent run narration">
        <div className="home-feed-head">
          <span className="home-feed-title">runs · narrated</span>
          <a className="home-feed-more" href="#runs">
            all runs →
          </a>
        </div>
        {feedRows.length === 0 ? (
          <p className="home-feed-empty">No runs yet — the first scheduled run will narrate here.</p>
        ) : (
          <ul className="home-beats">
            {feedRows.map((row) => {
              const kind = beatKind(row);
              const lines = beatLines(row, artifactById.get(row.run_id));
              const pill = statusPill(row.status);
              return (
                <li className={`home-beat home-beat--${kind}`} key={row.run_id}>
                  <span className="home-beat-t">{beatTime(row.completed_at)}</span>
                  <span className="home-beat-body">
                    <span className="home-beat-meta">
                      <span className="home-beat-routine">
                        {typeof row.routine === 'string' ? row.routine : 'run'}
                      </span>
                      <span className={pill.className}>{pill.label}</span>
                    </span>
                    {lines.map((line) => (
                      <span className="home-beat-text" key={line}>
                        {line}
                      </span>
                    ))}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Ledger positions panel — the SHARED drilldown body behind both the shadow
// p/l and the strategy p/l cells. Strategy legs are the EXECUTED counterpart of
// shadow legs (identical data shape, identical columns), so a single component
// renders both: the open-positions table (the primary content, with the stale
// badge + strategy column) and a de-emphasized closed-positions section (with
// the close reason). It takes the query result + an `emptyLabel` so each caller
// supplies its own "nothing here yet" copy; everything else is identical.
// ---------------------------------------------------------------------------

interface LedgerQuery {
  data?: { open: ShadowPosition[]; closed: ShadowPosition[] };
  isLoading: boolean;
  isError: boolean;
  error: unknown;
}

function LedgerPositionsBody({
  query,
  emptyLabel,
}: {
  query: LedgerQuery;
  emptyLabel: string;
}) {
  if (query.isLoading) return <p className="detail-panel-status">Loading positions…</p>;
  if (query.isError) {
    return (
      <p className="detail-panel-status error" role="alert">
        Failed to load positions:{' '}
        {query.error instanceof Error ? query.error.message : 'unknown error'}
      </p>
    );
  }

  const open = query.data?.open ?? [];
  const closed = query.data?.closed ?? [];

  if (open.length === 0 && closed.length === 0) {
    return <p className="detail-panel-status">{emptyLabel}</p>;
  }

  return (
    <>
      {open.length > 0 && (
        <table className="detail-table">
          <thead>
            <tr>
              <th>Position</th>
              <th>Strategy</th>
              <th className="detail-num">Qty</th>
              <th className="detail-num">Entry</th>
              <th className="detail-num">Mark</th>
              <th className="detail-num">Unrealized P/L</th>
              <th>Opened</th>
              <th>Target exit</th>
            </tr>
          </thead>
          <tbody>
            {open.map((pos) => {
              const exit = targetExit(pos.trade);
              return (
                <tr key={pos.data_key}>
                  <td className="detail-pos">{positionLabel(pos.trade)}</td>
                  <td className="shadow-strategy">{strategyText(pos)}</td>
                  <td className="detail-num">{qtyText(pos.trade.quantity)}</td>
                  <td className="detail-num">{fmtPrice(pos.entry)}</td>
                  <td className="detail-num">
                    {fmtPrice(pos.last_mark)}
                    {pos.mark_stale && <span className="shadow-stale badge">stale</span>}
                  </td>
                  <td className={`detail-num glance-value${signClass(pos.unrealized_pnl)}`}>
                    {fmtSignedMoney(pos.unrealized_pnl)}
                  </td>
                  <td>{dateOnly(pos.opened_at)}</td>
                  <td>{exit ?? '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {closed.length > 0 && (
        <div className="shadow-closed">
          <span className="shadow-closed-title">recently closed</span>
          <table className="detail-table detail-table--closed">
            <thead>
              <tr>
                <th>Position</th>
                <th className="detail-num">Realized P/L</th>
                <th>Close reason</th>
                <th className="detail-num">Exit</th>
                <th>Closed</th>
              </tr>
            </thead>
            <tbody>
              {closed.map((pos) => (
                <tr key={pos.data_key}>
                  <td className="detail-pos">{positionLabel(pos.trade)}</td>
                  <td className={`detail-num glance-value${signClass(pos.realized_pnl)}`}>
                    {fmtSignedMoney(pos.realized_pnl)}
                  </td>
                  <td>{typeof pos.close_reason === 'string' ? pos.close_reason : '—'}</td>
                  <td className="detail-num">{fmtPrice(pos.exit_price)}</td>
                  <td>{dateOnly(pos.closed_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Book positions panel — the drilldown behind the merged Book cell. Renders
// loading (artifact not yet fetched) / empty / table states for the held book
// positions read from the newest run artifact's top-level `positions` array.
// Per-row P/L is the position's OWN day and since-OPENED (cost-basis) drift —
// deliberately NOT summed to the face's account-level headline.
// ---------------------------------------------------------------------------

function BookPanelBody({ artifact, loaded }: { artifact: unknown; loaded: boolean }) {
  if (!loaded) return <p className="detail-panel-status">Loading book positions…</p>;

  const positions = bookPositions(artifact);
  if (positions.length === 0) {
    return <p className="detail-panel-status">No book positions.</p>;
  }

  return (
    <table className="detail-table">
      <thead>
        <tr>
          <th>Symbol</th>
          <th className="detail-num">Qty</th>
          <th className="detail-num">Avg Entry</th>
          <th className="detail-num">Price</th>
          <th className="detail-num">Day P/L</th>
          {/* "Since open" = each position's gain/loss vs its own cost basis
              (when it was opened) — NOT the headline's since-inception basis. */}
          <th className="detail-num" title="Per-position gain/loss since opened (vs cost basis)">
            Since Open P/L
          </th>
        </tr>
      </thead>
      <tbody>
        {positions.map((pos, i) => {
          const r = bookRow(pos);
          return (
            <tr key={`${r.symbol}-${i}`}>
              <td className="detail-pos">{r.symbol}</td>
              <td className="detail-num">{r.qty}</td>
              <td className="detail-num">{r.avgEntry}</td>
              <td className="detail-num">{r.price}</td>
              <td className="detail-num">
                <span className={`glance-value${r.dayClass}`}>{r.dayPnl}</span>
                {r.dayPct && <span className="detail-pct"> {r.dayPct}</span>}
              </td>
              <td className="detail-num">
                <span className={`glance-value${r.sinceOpenClass}`}>{r.sinceOpenPnl}</span>
                {r.sinceOpenPct && <span className="detail-pct"> {r.sinceOpenPct}</span>}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
